import React, { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import { buildYouthStandingsFromFixtures } from "../utils/youthStandings";
import { buildStandingsFromFixtures, compareStandingRows, withMovement } from "../utils/standingsFromFixtures";
import { Link } from "react-router-dom";
import { Box, Skeleton, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import TeamCrest from "./ui/TeamCrest";
import ShareButton from "./ui/ShareButton";
import MovementArrow from "./ui/MovementArrow";
import { seasonLabel } from "../utils/leagueCatalog";

const Standings = ({
  city,
  division,
  sport = "Soccer",
  ageGroup = "Adult",
  /** When true, load youth tables and compute points from completed youth fixtures. */
  youthMode = false,
  /** When true, compute W/L/D/pts from fixture scores instead of stored team columns. */
  computeFromFixtures = false,
  season = null,
  showTitle = true,
}) => {
  const [standings, setStandings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fixtures, setFixtures] = useState([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    let cancelled = false;
    const fetchStandingsAndFixtures = async () => {
      try {
        if (youthMode) {
          const { data: teamsData, error: teamsError } = await supabase
            .from("youth_teams")
            .select("id, name, wins, losses, draws, goalsFor, goalsAgainst, logo_url");
          if (teamsError) throw teamsError;
          const { data: fxData, error: fxError } = await supabase
            .from("youth_fixtures")
            .select("home_team_id, away_team_id, home_team_score, away_team_score, date")
            .order("date", { ascending: false });
          if (fxError) throw fxError;

          const computed = buildYouthStandingsFromFixtures(fxData || [], teamsData || []);
          const merged = computed.map((row) => {
            const t = (teamsData || []).find((x) => x.id === row.id);
            return {
              id: row.id,
              name: row.name,
              wins: row.wins,
              losses: row.losses,
              draws: row.draws,
              goalsFor: row.goalsFor,
              goalsAgainst: row.goalsAgainst,
              goalDifference: row.goalDifference,
              points: row.points,
              gp: row.gp ?? row.wins + row.draws + row.losses,
              logo_url: t?.logo_url ?? null,
            };
          });
          !cancelled && setFixtures(fxData || []);
          !cancelled && setStandings(withMovement(merged, fxData));
          return;
        }

        let teamsQuery = supabase
          .from("teams")
          .select(
            "id, name, wins, losses, draws, forfeits, goalsFor, goalsAgainst, logo_url, division, season"
          )
          .eq("city", city)
          .eq("sport", sport);

        const { data: teamsData, error: teamsError } = await teamsQuery;
        if (teamsError) throw teamsError;

        let fxQuery = supabase
          .from("fixtures")
          .select(
            "home_team_id, away_team_id, home_team_score, away_team_score, date, is_forfeit"
          )
          .eq("city", city)
          .eq("sport", sport)
          .eq("age_group", ageGroup)
          .order("date", { ascending: false });
        if (season) fxQuery = fxQuery.eq("season", season);

        if (city === "Cambridge" || city === "Mississauga") {
          if (!division) {
            !cancelled && setStandings([]);
            !cancelled && setFixtures([]);
            return;
          }
          fxQuery = fxQuery.ilike("division", division);
        }

        const { data: fixturesData, error: fixturesError } = await fxQuery;
        if (fixturesError) throw fixturesError;

        !cancelled && setFixtures(fixturesData || []);

        let filteredTeams = teamsData || [];

        if (city === "Cambridge") {
          const teamIds = new Set();
          (fixturesData || []).forEach((f) => {
            if (f.home_team_id) teamIds.add(f.home_team_id);
            if (f.away_team_id) teamIds.add(f.away_team_id);
          });
          filteredTeams = filteredTeams.filter((t) => teamIds.has(t.id));
        }

        if (city === "Mississauga" && division) {
          const divKey = division.trim().toUpperCase();
          filteredTeams = filteredTeams.filter(
            (t) =>
              (t.division || "").trim().toUpperCase() === divKey &&
              (!season || t.season === season)
          );
        }

        let calculated;
        if (computeFromFixtures) {
          const computed = buildStandingsFromFixtures(fixturesData || [], filteredTeams);
          calculated = computed.map((row) => {
            const t = filteredTeams.find((x) => x.id === row.id);
            return { ...row, logo_url: t?.logo_url ?? null };
          });
        } else {
          calculated = filteredTeams.map((team) => ({
            ...team,
            points: team.wins * 3 + team.draws,
            goalDifference: team.goalsFor - team.goalsAgainst,
            gp:
              (team.wins || 0) +
              (team.losses || 0) +
              (team.draws || 0) +
              (team.forfeits || 0),
          }));
          calculated.sort(compareStandingRows);
        }

        !cancelled && setStandings(withMovement(calculated, fixturesData));
      } catch (error) {
        console.error("Error fetching standings or fixtures:", error);
        !cancelled && setStandings([]);
        !cancelled && setFixtures([]);
      }
    };

    setLoading(true);
    fetchStandingsAndFixtures().finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [city, division, sport, ageGroup, youthMode, computeFromFixtures, season]);

  const getTeamForm = (teamId) =>
    fixtures
      .filter((f) => f.home_team_id === teamId || f.away_team_id === teamId)
      .filter((f) => f.is_forfeit || (f.home_team_score !== null && f.away_team_score !== null))
      .slice(0, 5)
      .map((f) => {
        if (f.is_forfeit) return "F";
        const isHome = f.home_team_id === teamId;
        const teamScore = isHome ? f.home_team_score : f.away_team_score;
        const oppScore = isHome ? f.away_team_score : f.home_team_score;
        if (teamScore > oppScore) return "W";
        if (teamScore < oppScore) return "L";
        return "D";
      })
      .reverse();

  const statCols = isMobile
    ? [
        { key: "gp", label: "GP" },
        { key: "goalDifference", label: "GD" },
        { key: "points", label: "Pts", strong: true },
      ]
    : [
        { key: "gp", label: "GP" },
        { key: "wins", label: "W" },
        { key: "draws", label: "D" },
        { key: "losses", label: "L" },
        { key: "goalsFor", label: "GF" },
        { key: "goalsAgainst", label: "GA" },
        { key: "goalDifference", label: "GD" },
        { key: "points", label: "Pts", strong: true },
      ];
  const gridCols = isMobile
    ? `44px minmax(0, 1fr) ${statCols.map(() => "40px").join(" ")}`
    : `60px minmax(0, 1fr) ${statCols.map(() => "48px").join(" ")} 150px`;

  const fmt = (key, v) => {
    const n = Number(v) || 0;
    if (key === "goalDifference" && n > 0) return `+${n}`;
    return n;
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        px: isMobile ? 1.5 : 3,
        pb: 6,
      }}
    >
      {showTitle ? (
        <Typography sx={{ textAlign: "center", fontWeight: 800, mt: 3, mb: 2, color: "#1a1a1a", fontSize: isMobile ? "1.6rem" : "2rem" }}>
          Standings
        </Typography>
      ) : (
        <Box sx={{ height: 16 }} />
      )}

      {!loading && standings.length > 0 && (
        <Box sx={{ width: "100%", maxWidth: 1000, display: "flex", justifyContent: "flex-end", mb: 1.25 }}>
          <ShareButton
            kind="standings"
            variant="dark"
            label="Share table"
            data={{
              title: "Standings",
              subtitle: youthMode ? "HSL Youth" : [city, division, season ? seasonLabel(season) : null].filter(Boolean).join(" · "),
              rows: standings,
              division: youthMode ? null : division,
              city: youthMode ? null : city,
            }}
          />
        </Box>
      )}

      <Box
        sx={{
          width: "100%",
          maxWidth: 1000,
          backgroundColor: "#fff",
          borderRadius: 4,
          border: "1px solid #ececec",
          boxShadow: "0 1px 2px rgba(16,24,40,0.04), 0 4px 14px rgba(16,24,40,0.05)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: gridCols,
            alignItems: "center",
            columnGap: 0.5,
            px: 1.5,
            py: 1.25,
            backgroundColor: "#fafafa",
            borderBottom: "1px solid #f0f0f0",
            fontSize: "0.7rem",
            fontWeight: 800,
            letterSpacing: "0.08em",
            color: "#8a8a8a",
            textTransform: "uppercase",
          }}
        >
          <Box sx={{ textAlign: "center" }}>#</Box>
          <Box sx={{ pl: 0.5 }}>Club</Box>
          {statCols.map((c) => (
            <Box key={c.key} sx={{ textAlign: "center", color: c.strong ? "#1a1a1a" : undefined }}>
              {c.label}
            </Box>
          ))}
          {!isMobile && <Box sx={{ textAlign: "center" }}>Form</Box>}
        </Box>

        {loading ? (
          <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} variant="rounded" height={36} />
            ))}
          </Box>
        ) : standings.length === 0 ? (
          <Box sx={{ py: 4, px: 2, textAlign: "center" }}>
            <Typography sx={{ fontWeight: 800, color: "#111" }}>No table yet</Typography>
            <Typography sx={{ mt: 0.5, color: "#8a8a8a", fontSize: "0.88rem" }}>
              Standings appear once {division || "this league"} has published fixtures.
            </Typography>
          </Box>
        ) : (
          standings.map((team, index) => {
            const form = getTeamForm(team.id);
            const isLeader = index === 0 && (Number(team.gp) || 0) > 0;
            return (
              <Box
                key={team.id}
                sx={{
                  display: "grid",
                  gridTemplateColumns: gridCols,
                  alignItems: "center",
                  columnGap: 0.5,
                  px: 1.5,
                  py: isMobile ? 1 : 1.1,
                  borderBottom: "1px solid #f4f4f4",
                  "&:last-of-type": { borderBottom: "none" },
                  boxShadow: isLeader ? "inset 3px 0 0 #1a1a1a" : "none",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                  <Box
                    sx={{
                      width: 24,
                      flexShrink: 0,
                      height: 24,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "0.75rem",
                      fontWeight: 800,
                      backgroundColor: isLeader ? "#1a1a1a" : "transparent",
                      color: isLeader ? "#fff" : "#8a8a8a",
                    }}
                  >
                    {index + 1}
                  </Box>
                  <MovementArrow movement={team.movement} showCount={!isMobile} sx={{ minWidth: isMobile ? 11 : 20 }} />
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0, pl: 0.5 }}>
                  <Box sx={{ width: 32, height: 32, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <TeamCrest team={team} size={32} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      noWrap
                      {...(youthMode ? {} : { component: Link, to: `/team/${team.id}` })}
                      sx={{
                        display: "block",
                        fontSize: isMobile ? "0.88rem" : "0.95rem",
                        fontWeight: 700,
                        color: "#1a1a1a",
                        textDecoration: "none",
                        "&:hover": { textDecoration: youthMode ? "none" : "underline" },
                      }}
                    >
                      {team.name}
                    </Typography>
                    {isMobile && form.length > 0 && (
                      <Box sx={{ display: "flex", gap: 0.4, mt: 0.4 }}>
                        {form.slice(-3).map((r, i) => (
                          <FormBadge key={i} result={r} small />
                        ))}
                      </Box>
                    )}
                  </Box>
                </Box>

                {statCols.map((c) => (
                  <Typography
                    key={c.key}
                    sx={{
                      textAlign: "center",
                      fontVariantNumeric: "tabular-nums",
                      fontSize: c.strong ? "0.98rem" : "0.85rem",
                      fontWeight: c.strong ? 800 : 500,
                      color:
                        c.key === "goalDifference" && Number(team[c.key]) < 0
                          ? "#c62828"
                          : c.key === "goalDifference" && Number(team[c.key]) > 0
                          ? "#2e7d32"
                          : c.strong
                          ? "#1a1a1a"
                          : "#555",
                    }}
                  >
                    {fmt(c.key, team[c.key])}
                  </Typography>
                ))}

                {!isMobile && (
                  <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5 }}>
                    {Array.from({ length: 5 }).map((_, i) => {
                      const r = form[i];
                      return r ? <FormBadge key={i} result={r} /> : <FormBadge key={i} />;
                    })}
                  </Box>
                )}
              </Box>
            );
          })
        )}
      </Box>

      <Box sx={{ mt: 1.5, display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 1.5, color: "#8a8a8a", fontSize: "0.72rem" }}>
        {[
          ["W", "Win"],
          ["D", "Draw"],
          ["L", "Loss"],
          ["F", "Forfeit (no points)"],
        ].map(([r, label]) => (
          <Box key={r} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <FormBadge result={r} small />
            <span>{label}</span>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const FORM_COLORS = {
  W: { bg: "#2e7d32", fg: "#fff" },
  D: { bg: "#9e9e9e", fg: "#fff" },
  L: { bg: "#c62828", fg: "#fff" },
  F: { bg: "#1a1a1a", fg: "#fff" },
};

const FormBadge = ({ result, small = false }) => {
  const size = small ? 16 : 22;
  const c = FORM_COLORS[result];
  return (
    <Box
      title={result === "F" ? "Forfeit" : undefined}
      sx={{
        width: size,
        height: size,
        borderRadius: small ? "4px" : "6px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: small ? "0.58rem" : "0.68rem",
        fontWeight: 800,
        backgroundColor: c ? c.bg : "transparent",
        color: c ? c.fg : "transparent",
        border: c ? "none" : "1.5px dashed #e0e0e0",
        boxSizing: "border-box",
        flexShrink: 0,
      }}
    >
      {result || ""}
    </Box>
  );
};

export default Standings;
